import React from "react";
import { checkRepositories } from "./repoApi";

// RepoBranchRows — one row per service: repository (existing from a dropdown or a
// typed new name) + branch + base branch, with live `check` state. Shared by the
// Save-to-repository dialog and the Deployment screen. Controlled-output via
// onRepos(repos, valid) where repos = [{ service, repo, branch?, baseBranch? }].
//
// Props:
//   provider    "github" | "azure-repos"
//   sessionId, backendUrl, token
//   creds       github:  { owner, token }   |  azure-repos: { orgUrl, pat, project }
//   units       [{ service, kind, suggestedRepo }]
//   projName    seed for the default branch name (smarthub/<projName>)
//   onRepos(repos, valid)

const C = {
  bg: "#0e1219", border: "rgba(255,255,255,0.1)", borderHi: "rgba(59,127,255,0.4)",
  accent: "#3b7fff", text: "#dce8ff", sub: "#aac0dc", muted: "#7a93bc", good: "#00c98d", warn: "#f5a623", bad: "#ef4444",
};
const slug = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 100);
const NAME_RE = /^[A-Za-z0-9._-]{1,100}$/;
const input = { width: "100%", background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 8px", fontSize: 12.5, outline: "none", boxSizing: "border-box" };
const mini = { fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.03em", marginBottom: 3 };
const selStyle = { ...input, appearance: "auto", cursor: "pointer" };
const linkBtn = { background: "none", border: "none", color: C.accent, fontSize: 10.5, cursor: "pointer", padding: "2px 0", marginTop: 2 };

const STATE_STYLE = {
  new: { label: "new", color: C.good },
  empty: { label: "empty", color: C.sub },
  "has-content": { label: "has content", color: C.warn },
};

export default function RepoBranchRows({ provider, sessionId, backendUrl, token, creds, units, projName, onRepos }) {
  const unitList = Array.isArray(units) ? units : [];
  const defaultBranch = `smarthub/${slug(projName) || "generated"}`;

  const [rows, setRows] = React.useState({});
  const [checks, setChecks] = React.useState({});
  const [checking, setChecking] = React.useState(false);
  const [ghRepos, setGhRepos] = React.useState([]);
  const debounceRef = React.useRef(null);

  // Seed one row per service. Repository starts UNSELECTED (dropdown shows "— select —");
  // the suggestedRepo is used only to prefill the name when the user picks "create new",
  // or to auto-select an existing match (effect below). Dropdown-first everywhere.
  React.useEffect(() => {
    setRows((prev) => {
      const next = {};
      for (const u of unitList) next[u.service] = prev[u.service] || { repo: "", branch: "", baseBranch: "" };
      return next;
    });
    setChecks({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(unitList.map((u) => u.service + ":" + (u.suggestedRepo || "")))]);

  // Existing-repo suggestions for the dropdown (GitHub REST allows browser calls).
  React.useEffect(() => {
    if (provider !== "github" || !(creds && creds.token)) { setGhRepos([]); return; }
    const ctrl = new AbortController();
    fetch("https://api.github.com/user/repos?per_page=100&sort=updated&affiliation=owner,collaborator,organization_member", {
      headers: { Authorization: `Bearer ${creds.token}`, Accept: "application/vnd.github+json" }, signal: ctrl.signal,
    })
      .then((r) => (r.ok ? r.json() : []))
      .then((list) => { if (Array.isArray(list)) setGhRepos(list.map((x) => x.full_name || x.name).filter(Boolean)); })
      .catch(() => {});
    return () => ctrl.abort();
  }, [provider, creds && creds.token]);

  const setRow = (svc, patch) => setRows((p) => ({ ...p, [svc]: { ...(p[svc] || { repo: "", branch: "", baseBranch: "" }), ...patch } }));

  const credsReady = provider === "github" ? !!(creds && creds.token && creds.owner) : !!(creds && creds.pat && creds.orgUrl);

  // Owned repo names (just the repo, no owner/) for the existing-repo dropdown.
  const repoNameOptions = React.useMemo(() => {
    const owner = ((creds && creds.owner) || "").toLowerCase();
    const names = ghRepos.map((fn) => {
      const i = fn.indexOf("/");
      return i >= 0 ? { owner: fn.slice(0, i).toLowerCase(), name: fn.slice(i + 1) } : { owner, name: fn };
    });
    const owned = owner ? names.filter((x) => x.owner === owner) : names;
    return Array.from(new Set((owned.length ? owned : names).map((x) => x.name).filter(Boolean)));
  }, [ghRepos, creds && creds.owner]);

  // Once the account's repos load, auto-select a suggestedRepo that already exists, so an
  // existing target opens pre-selected in the dropdown. A brand-new suggested name stays
  // unselected (dropdown + "➕ Create new repository…"). Never overrides a user choice.
  React.useEffect(() => {
    if (provider !== "github" || !repoNameOptions.length) return;
    const lc = new Map(repoNameOptions.map((n) => [n.toLowerCase(), n]));
    setRows((prev) => {
      let changed = false; const next = { ...prev };
      for (const u of unitList) {
        const r = next[u.service];
        if (!r || r.repoNew || (r.repo || "").trim()) continue;
        const hit = lc.get((u.suggestedRepo || "").trim().toLowerCase());
        if (hit) { next[u.service] = { ...r, repo: hit }; changed = true; }
      }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(repoNameOptions), provider, JSON.stringify(unitList.map((u) => u.service))]);

  // Build the check/save body for the current rows.
  const reposBody = React.useCallback(() => {
    return unitList.map((u) => {
      const r = rows[u.service] || {};
      const entry = { service: u.service, repo: (r.repo || "").trim() };
      if ((r.branch || "").trim()) entry.branch = r.branch.trim();
      if ((r.baseBranch || "").trim()) entry.baseBranch = r.baseBranch.trim();
      return entry;
    }).filter((e) => e.repo);
  }, [unitList, rows]);

  // Debounced check whenever rows or creds change.
  React.useEffect(() => {
    if (!credsReady) return;
    const repos = reposBody();
    if (!repos.length) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setChecking(true);
      const body = { sessionId, provider, repos };
      if (provider === "github") body.github = { token: creds.token, owner: creds.owner };
      else body.azureRepos = { orgUrl: creds.orgUrl, pat: creds.pat, project: creds.project };
      const { ok, data } = await checkRepositories(backendUrl, token, body);
      if (ok && data && data.success && Array.isArray(data.repos)) {
        const byService = {};
        for (const row of data.repos) byService[row.service] = row;
        setChecks(byService);
      }
      setChecking(false);
    }, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, credsReady, creds && creds.token, creds && creds.owner, creds && creds.pat, creds && creds.orgUrl, creds && creds.project, JSON.stringify(reposBody())]);

  // Duplicate repo detection (same repo on two rows is rejected).
  const repoCounts = {};
  for (const u of unitList) { const n = ((rows[u.service] || {}).repo || "").trim().toLowerCase(); if (n) repoCounts[n] = (repoCounts[n] || 0) + 1; }

  // Emit repos + validity to the parent.
  React.useEffect(() => {
    const repos = reposBody();
    let valid = repos.length === unitList.length; // every service has a repo
    const dup = Object.values(repoCounts).some((c) => c > 1);
    if (dup) valid = false;
    for (const u of unitList) {
      const r = rows[u.service] || {}; const chk = checks[u.service] || {};
      const name = (r.repo || "").trim();
      if (!NAME_RE.test(name) || name.startsWith(".")) valid = false;
      if (chk.state === "has-content") {
        if (!(r.branch || "").trim()) valid = false; // branch required
        if ((r.branch || "").trim() && chk.branchExists === false && (r.baseBranch || "").trim() && chk.baseBranchExists === false) valid = false;
      }
    }
    onRepos && onRepos(repos, valid && credsReady);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(rows), JSON.stringify(checks), credsReady]);

  const summary = (svc) => {
    const r = rows[svc] || {}; const chk = checks[svc] || {};
    const br = (r.branch || "").trim();
    if (chk.state === "new" || chk.state === "empty") return br ? `New repo — code on main and ${br}` : "New repo — code on main";
    if (chk.state === "has-content") {
      if (!br) return "";
      if (chk.branchExists) return `Push onto existing ${br}`;
      return `Will create ${br} from ${(r.baseBranch || "").trim() || chk.defaultBranch || "default branch"}`;
    }
    return "";
  };

  return (
    <div>
      <div style={{ fontSize: 12, color: C.sub, margin: "2px 0 8px" }}>
        {unitList.length} service{unitList.length === 1 ? "" : "s"} — pick a repository (existing or create new){checking ? " · checking…" : ""}:
      </div>
      {unitList.map((u) => {
        const r = rows[u.service] || {}; const chk = checks[u.service] || {};
        const st = STATE_STYLE[chk.state];
        const dup = ((r.repo || "").trim() && repoCounts[(r.repo || "").trim().toLowerCase()] > 1);
        const branches = chk.branches || [];
        const showBase = chk.state === "has-content" && (r.branch || "").trim() && chk.branchExists === false;
        const branchMissing = chk.state === "has-content" && !(r.branch || "").trim();
        // Repository: dropdown of owned repos (GitHub) + "create new"; falls back to a
        // text field for Azure or before the repo list has loaded.
        const hasRepoList = provider === "github" && repoNameOptions.length > 0;
        const repoVal = (r.repo || "").trim();
        // Dropdown-first: the create-new text box shows only on an explicit "create new"
        // choice, never because the seeded suggestion doesn't exist yet.
        const repoIsNew = !!r.repoNew;
        // Branch: dropdown of the repo's existing branches + "new branch"; text field
        // when the repo is new/empty (no branches yet).
        const branchVal = (r.branch || "").trim();
        const branchIsNew = !!r.branchNew || (!!branchVal && branches.length > 0 && !branches.includes(branchVal));
        return (
          <div key={u.service} style={{ marginBottom: 12, paddingBottom: 12, borderBottom: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5 }}>
              <span style={{ fontSize: 12.5, fontWeight: 700, color: C.text, fontFamily: "monospace", wordBreak: "break-word" }}>{u.service}</span>
              {u.kind && <span style={{ fontSize: 9.5, fontWeight: 700, color: C.muted, border: `1px solid ${C.border}`, borderRadius: 4, padding: "1px 5px", textTransform: "uppercase" }}>{u.kind}</span>}
              {st && <span style={{ fontSize: 10, fontWeight: 700, color: st.color }}>{st.label}</span>}
            </div>
            <div style={{ display: "flex", gap: 6, alignItems: "flex-start" }}>
              <div style={{ flex: 2 }}>
                <div style={mini}>Repository</div>
                {hasRepoList && !repoIsNew ? (
                  <select style={{ ...selStyle, borderColor: dup ? C.bad : C.border }}
                    value={repoNameOptions.includes(repoVal) ? repoVal : ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "__new__") setRow(u.service, { repoNew: true, repo: repoVal || slug(u.suggestedRepo) });
                      else setRow(u.service, { repoNew: false, repo: v });
                    }}>
                    <option value="">— select a repository —</option>
                    {repoNameOptions.map((n) => <option key={n} value={n}>{n}</option>)}
                    <option value="__new__">➕ Create new repository…</option>
                  </select>
                ) : (
                  <input style={{ ...input, borderColor: dup ? C.bad : C.border }}
                    value={r.repo || ""} autoFocus={repoIsNew && hasRepoList}
                    onChange={(e) => setRow(u.service, { repo: e.target.value, repoNew: hasRepoList ? true : undefined })}
                    placeholder={provider === "github" ? "new repository name" : "existing or new repository name"} />
                )}
                {hasRepoList && repoIsNew && (
                  <button type="button" style={linkBtn} onClick={() => setRow(u.service, { repoNew: false, repo: "" })}>↩ pick existing</button>
                )}
                {dup && <div style={{ fontSize: 10, color: C.bad }}>Same repository used twice.</div>}
              </div>
              <div style={{ flex: 1.3 }}>
                <div style={mini}>Branch{chk.state === "has-content" ? " *" : ""}</div>
                {branches.length > 0 && !branchIsNew ? (
                  <select style={{ ...selStyle, borderColor: branchMissing ? C.bad : C.border }}
                    value={branches.includes(branchVal) ? branchVal : ""}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "__new__") setRow(u.service, { branchNew: true, branch: defaultBranch });
                      else setRow(u.service, { branchNew: false, branch: v });
                    }}>
                    <option value="">— select a branch —</option>
                    {branches.map((b) => <option key={b} value={b}>{b}</option>)}
                    <option value="__new__">➕ New branch…</option>
                  </select>
                ) : (
                  <input style={{ ...input, borderColor: branchMissing ? C.bad : C.border }}
                    value={r.branch || ""} onChange={(e) => setRow(u.service, { branch: e.target.value, branchNew: branches.length > 0 ? true : undefined })}
                    placeholder={defaultBranch} />
                )}
                {branches.length > 0 && branchIsNew && (
                  <button type="button" style={linkBtn} onClick={() => setRow(u.service, { branchNew: false, branch: "" })}>↩ pick existing</button>
                )}
              </div>
              {showBase && (
                <div style={{ flex: 1.3 }}>
                  <div style={mini}>From (base)</div>
                  {branches.length > 0 ? (
                    <select style={{ ...selStyle, borderColor: chk.baseBranchExists === false && (r.baseBranch || "").trim() ? C.bad : C.border }}
                      value={r.baseBranch !== undefined && r.baseBranch !== "" ? r.baseBranch : (chk.defaultBranch || "main")}
                      onChange={(e) => setRow(u.service, { baseBranch: e.target.value })}>
                      {branches.map((b) => <option key={b} value={b}>{b}</option>)}
                    </select>
                  ) : (
                    <input style={{ ...input, borderColor: chk.baseBranchExists === false && (r.baseBranch || "").trim() ? C.bad : C.border }}
                      value={r.baseBranch !== undefined && r.baseBranch !== "" ? r.baseBranch : (chk.defaultBranch || "main")}
                      onChange={(e) => setRow(u.service, { baseBranch: e.target.value })} placeholder={chk.defaultBranch || "main"} />
                  )}
                  {chk.baseBranchExists === false && (r.baseBranch || "").trim() && <div style={{ fontSize: 10, color: C.bad }}>branch not found</div>}
                </div>
              )}
            </div>
            {branchMissing && <div style={{ fontSize: 10.5, color: C.bad, marginTop: 3 }}>A branch is required — this repository already has content.</div>}
            {summary(u.service) && <div style={{ fontSize: 10.5, color: C.muted, marginTop: 3 }}>{summary(u.service)}</div>}
          </div>
        );
      })}
    </div>
  );
}
